import React from 'react';
import { LayoutGrid, Plug, Zap, Layers, UserCircle, GraduationCap, ListChecks, Activity, MessagesSquare, FolderOpen, Monitor, ChevronRight, ChevronsUpDown, Bookmark, Plus, Settings } from 'lucide-react';
const NAV_GROUPS: {
  label?: string;
  items: {
    label: string;
    icon: React.ElementType;
    active?: boolean;
    chevron?: boolean;
  }[];
}[] = [{
  items: [{
    label: 'Home',
    icon: LayoutGrid
  }]
}, {
  label: 'Connect',
  items: [{
    label: 'Integrations',
    icon: Plug
  }, {
    label: 'Power Ups',
    icon: Zap
  }]
}, {
  label: 'Manage',
  items: [{
    label: 'Groups & Folders',
    icon: Layers
  }, {
    label: 'Assistants',
    icon: UserCircle
  }, {
    label: 'Skills',
    icon: GraduationCap
  }, {
    label: 'Workflows',
    icon: ListChecks,
    active: true
  }, {
    label: 'Pulses',
    icon: Activity
  }, {
    label: 'Chats',
    icon: MessagesSquare,
    chevron: true
  }]
}, {
  label: 'Brain',
  items: [{
    label: 'Files',
    icon: FolderOpen
  }, {
    label: 'Viewer',
    icon: Monitor
  }]
}];
/**
 * The product chrome for the workflow mockup: workspace sidebar on the left,
 * a slim top bar, and a scrolling content well.
 */
export function WorkflowAppShell({
  children
}: {
  children: React.ReactNode;
}) {
  return <div className="w-full min-h-screen bg-[#F6F5F3] font-sans text-[#2B211A] flex" data-id="element-6348" data-mp="qn4dq9r1p8jj">
      <aside className="hidden lg:flex w-[248px] shrink-0 flex-col border-r border-[#E7E3DD] bg-white" data-id="element-6349" data-mp="cvzm9uxtzmka">
        <div className="h-[68px] px-4 flex items-center gap-2.5 border-b border-[#EFEBE5]" data-id="element-6350" data-mp="hmfxr9i3cy40">
          <div className="w-8 h-8 rounded-lg bg-[#2B211A] text-white flex items-center justify-center font-title font-bold text-sm shrink-0" data-id="element-6351" data-mp="2xtljgnrgmh0">
            G
          </div>
          <span className="font-title font-bold text-[15px] flex-1 truncate" data-id="element-6352" data-mp="7x659sjc5dfd">
            Giga Main
          </span>
          <ChevronsUpDown size={15} className="text-neutral-400" data-id="element-6353" data-mp="3q4vmlq750t4c" />
        </div>

        <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-5" aria-label="Workspace" data-id="element-6354" data-mp="3v0t07or0nldf">
          {NAV_GROUPS.map((group, gi) => <div key={group.label ?? gi} data-id="element-6355" data-mp="1vunpij391x08" data-mpi={gi}>
              {group.label && <p className="px-2.5 mb-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-neutral-400" data-id="element-6356" data-mp="1vt4a0upf3pzi">
                  {group.label}
                </p>}
              <ul className="space-y-0.5" data-id="element-6357" data-mp="1muisn2vomh0b">
                {group.items.map(item => <li key={item.label} data-id="element-6358" data-mp="1ms63a4nlh4d8">
                    <a href="#" aria-current={item.active ? 'page' : undefined} className={`flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-[14px] transition-colors ${item.active ? 'bg-[#2B211A] text-white font-semibold' : 'text-[#4A423B] hover:bg-[#F6F5F3] font-medium'}`} data-id="element-6359" data-mp="2jkhz8rs9v5g9">
                      <item.icon size={16} className={item.active ? 'text-white' : 'text-neutral-500'} data-id="element-6360" data-mp="25l07c3jojc2o" />
                      <span className="flex-1 truncate" data-id="element-6361" data-mp="z4y13vlgqbff">{item.label}</span>
                      {item.chevron && <ChevronRight size={14} className="text-neutral-400" data-id="element-6362" data-mp="u4seitu1vf47" />}
                    </a>
                  </li>)}
              </ul>
            </div>)}
        </nav>

        <div className="border-t border-[#EFEBE5] px-3 py-3 flex items-center gap-2.5" data-id="element-6363" data-mp="35qua12s81wxx">
          <div className="w-8 h-8 rounded-full bg-[#FFEDD5] text-[#EA580C] flex items-center justify-center font-title font-bold text-xs" data-id="element-6364" data-mp="3apnp2zn5pw07">
            T
          </div>
          <div className="flex-1 min-w-0" data-id="element-6365" data-mp="3jkv9jyqzznzc">
            <p className="text-[13px] font-semibold truncate flex items-center gap-1.5" data-id="element-6366" data-mp="3jlwk8243q8gj">
              Tony Ennis
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" aria-hidden="true" data-id="element-6367" data-mp="3thzphgu92n7p" />
            </p>
            <p className="text-[11px] text-neutral-500 truncate" data-id="element-6368" data-mp="2bf34af543h0">Pro workspace</p>
          </div>
          <button type="button" className="text-neutral-400 hover:text-[#2B211A] transition-colors" aria-label="Workspace settings" data-id="element-6369" data-mp="6e1yumexm8jh">
            <Settings size={15} data-id="element-6370" data-mp="244moo1lcjhbc" />
          </button>
        </div>
      </aside>

      <div className="flex-1 min-w-0 flex flex-col" data-id="element-6371" data-mp="284526cau8q45">
        <header className="h-[68px] shrink-0 bg-white border-b border-[#E7E3DD] px-4 md:px-6 flex items-center gap-3" data-id="element-6372" data-mp="286q411b4z61w">
          <div className="lg:hidden w-8 h-8 rounded-lg bg-[#2B211A] text-white flex items-center justify-center font-title font-bold text-sm" data-id="element-6373" data-mp="18tetjmj1wook">
            G
          </div>
          <div className="flex items-center gap-1.5" data-id="element-6374" data-mp="1mqtloz0ovak8">
            <span className="w-7 h-7 rounded-md bg-[#6D28D9] text-white text-[11px] font-bold flex items-center justify-center" data-id="element-6375" data-mp="1huutf0c8p8ej">
              AH
            </span>
            <span className="w-7 h-7 rounded-md bg-[#4F46E5] text-white text-[11px] font-bold flex items-center justify-center" data-id="element-6376" data-mp="p55oibweso7n">
              Gg
            </span>
          </div>
          <button type="button" className="text-neutral-400 hover:text-[#2B211A] transition-colors" aria-label="Bookmarks" data-id="element-6377" data-mp="13vv55tsn2viw">
            <Bookmark size={16} data-id="element-6378" data-mp="bdhh2lx3unx0" />
          </button>
          <div className="flex-1" data-id="element-6379" data-mp="bc69eb0jgw0m" />
          <button type="button" className="inline-flex items-center gap-1.5 rounded-xl bg-[#2B211A] px-3.5 py-2 text-[13px] font-semibold text-white hover:bg-[#EA580C] transition-colors" data-id="element-6380" data-mp="6eo0gkmiugsm">
            <Plus size={15} data-id="element-6381" data-mp="2i06o65orbqm1" />
            New chat
          </button>
        </header>

        <main className="flex-1 overflow-y-auto" data-id="element-6382" data-mp="2mut14dahlr88">{children}</main>
      </div>
    </div>;
}